import dayjs from 'dayjs'
import { getEndpointPath } from '../services/api-request'
import { reportFailure } from '../services/report-failure'
import { buildCollaborationRoomName } from './collaboration-room'

const WEBSITE_URL = process.env.REACT_APP_WEB_HOST

export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

export const parseGenre = (genre) => {
  try {
    return JSON.parse(genre).join(', ')
  } catch (_) {
    return genre
  }
}

export const validateEmail = (email) => {
  const pattern = /^[\w.\-_+!]+@[\w.-]+\.\w{2,}$/
  return pattern.test(email)
}

export const hexToRgbA = (hex) => {
  let c
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('')
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]]
    }
    c = '0x' + c.join('')
    return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',0.14)'
  }
  throw new Error('Bad Hex')
}

export const getUserData = (user) => {
  return {
    userId: user.uid,
    displayName: user.displayName,
    photoURL: user.photoURL
  }
}

export const getElemContent = (blockId, parentElement) => {
  let blockElement
  if (parentElement) {
    blockElement = parentElement.querySelector(`[id='${blockId}']`)
  } else {
    blockElement = document.getElementById(blockId)
  }
  return blockElement && blockElement.innerHTML
}

const extensionOf = (fileName) => String(fileName || '').split('.').pop().toLowerCase() || undefined

export const downloadUrl = (href, fileName) => {
  const a = document.createElement('a')
  a.setAttribute('download', fileName)
  a.setAttribute('href', href)
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

// An empty file is reported and thrown, so the caller's error toast fires
// instead of the user silently saving a 0-byte file.
export const downloadBlobFile = (blob, fileName, { feature } = {}) => {
  if (!blob || !blob.size) {
    const error = new Error('The exported file was empty. Please try again.')
    reportFailure({ feature, scenario: 'export', exportType: extensionOf(fileName), error, extra: { fileName, blobType: blob?.type } })
    throw error
  }
  // Check the Browser type and download the File.
  const isIE = !!document.documentMode
  if (isIE) {
    window.navigator.msSaveBlob(blob, fileName)
    return
  }
  const url = window.URL || window.webkitURL
  const link = url.createObjectURL(blob)
  downloadUrl(link, fileName)
  // Revoking in the same tick can cancel the download in Safari and Firefox.
  setTimeout(() => url.revokeObjectURL(link), 1000)
}

// Opens a generated file in a new tab, falling back to a download when the
// browser blocks the tab. Safari blocks any tab opened after an await, so
// `afterAwait` callers skip straight to the download there.
export const openExportUrl = (url, fileName, { feature, afterAwait = false } = {}) => {
  const skipTab = afterAwait && isSafariBrowser()
  const tab = skipTab ? null : window.open(url, '_blank')
  if (tab) {
    tab.opener = null
    return
  }
  if (!skipTab) {
    reportFailure({
      feature,
      scenario: 'export',
      exportType: extensionOf(fileName),
      level: 'warning',
      error: new Error('Export tab blocked by the browser'),
      extra: { fileName, fallback: 'download' }
    })
  }
  downloadUrl(url, fileName)
}

export const errorText = (error) => {
  if (error) {
    if (typeof error === 'string') {
      return error
    }
    return error.message || 'FinalBit can\'t suggest at this time please try again later!'
  }
  return 'Something went wrong'
}

export const navigateToPricing = () => {
  window.location.href = `${WEBSITE_URL}/pricing`
}

// Real Safari detection (Chrome, Edge and Firefox on iOS excluded).
export const isSafariBrowser = (userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '') =>
  /Version\/[\d.]+.*Safari/.test(userAgent) && !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|Android/.test(userAgent)

// Feature gate, not detection: `true` hides Storyboard and Pitch Deck, so it
// stays off. Use isSafariBrowser() to detect Safari.
export const isSafari = () => {
  // disable safari validation on development mode.
  return false
  /* if (process.env.NODE_ENV === 'development') {
    return false
  }
  return typeof navigator !== 'undefined' && /Version\/[\d.]+.*Safari/.test(navigator.userAgent) */
}

// functionality is  to support legacy script and validate types of the script
export const getScriptType = (scriptInfo) => {
  if (scriptInfo.type) return scriptInfo.type
  let type = 'featureFilm'
  try {
    const genre = parseGenre(scriptInfo.genre)
    if (genre.includes('Video Short')) {
      type = 'videoShort'
    } else {
      type = 'featureFilm'
    }
  } catch (err) {
    type = 'featureFilm'
  }
  return type
}

/* The validation error have a define structure
   We need iterate every validation error and show them in the popup
*/
export const getValidationError = (error) => {
  return error.errorFields?.length > 0 && error.errorFields.map((errorField) => errorField.errors.join(', ')).join('; ')
}

export const getUploadUrl = (file) => {
  const fileName = file.name
  const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase()

  switch (fileExtension) {
    case 'pdf':
      return getEndpointPath('script/parse_pdf')
    case 'txt':
    case 'fountain':
      return getEndpointPath('script/parse_fountain')
    case 'fdx':
      return getEndpointPath('script/parse_fdx')
  }
}

export const capitalizeFirstLetter = (word) => {
  if (!word) return ''
  return word[0].toLocaleUpperCase() + word.slice(1)
}

/**
 * Validates if the input is a string and matches one of the predefined content types
 * @param {any} value - The value to validate
 * @returns {boolean} - Returns true if valid, false otherwise
 */
export const validateContentType = (value) => {
  // First, check if it's a string
  if (typeof value !== 'string') {
    return false
  }

  // Define the valid content types
  const validContentTypes = [
    'featureFilm',
    'episodicPilot',
    'videoShort'
  ]

  // Check if the input string matches any of the valid content types
  return validContentTypes.includes(value)
}

export const generateSharedUrl = ({
  projectUuid,
  scriptUuid,
  sceneUuid
}, feature = 'editor') => buildCollaborationRoomName({
  projectUuid,
  scriptUuid,
  sceneUuid,
  feature
})

export const abbreviation = (n) => {
  if (!n) return ''
  return n.split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
}

export const fixDayJsObject = (dayjsObject) => {
  const {
    $y: year,
    $M: month, // zero-based (0=Jan)
    $D: date,
    $H: hour,
    $m: minute,
    $s: second,
    $ms: millisecond
  } = dayjsObject
  const d = new Date(
    year,
    month,
    date,
    hour,
    minute,
    second,
    millisecond
  )
  return dayjs(d)
}

const DAYJS_PROTO = Object.getPrototypeOf(dayjs())

export const isCorruptedDayjs = (obj) => {
  return (
    obj != null &&
    obj.$isDayjsObject === true &&
    Object.getPrototypeOf(obj) !== DAYJS_PROTO
  )
}
